import { get } from "lodash";
import { keyBy } from "lodash/fp";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import DisTable from "src/components/TableV2/Table";
import { IRootState } from "src/store/reducers";
import { fetchDocuments } from "src/store/reducers/documentation";
import { fetchProductBuilderOptions } from "src/store/reducers/productBuilder";
import { fetchProposalClassifications } from "src/store/reducers/proposalOptions";
import { iso8601ToShort } from "src/util/utils.dates";
import * as S from "../../constants/StringConstants";

import { Checkbox } from "src/components/Form/CheckboxFieldV2/CheckboxFieldV2";
import { IDocument } from "src/store/models/document.model";
import "./ConvertModal.scss";

const updateFileList = (attachedDocumentIDs, document) => {
  const id = parseInt(document.documentID, 10);
  const index = attachedDocumentIDs.indexOf(id);
  if (index > -1) {
    attachedDocumentIDs.splice(index, 1);
  } else {
    attachedDocumentIDs.push(id);
  }

  return [...attachedDocumentIDs];
};

const columns = (files, setFiles) => [
  {
    key: "",
    title: "",
    render: item => {
      const selected = files.includes(item.documentID);
      return (
        <Checkbox
          checked={selected}
          onClick={e => {
            setFiles(updateFileList(files, item));
          }}
        />
      );
    },
  },
  { key: "fileName", title: S.DOC_NAME_COL },
  { key: "modifiedBy", title: S.DOC_MODIFIED_BY_COL, style: { width: "120px" } },
  {
    key: "modifiedDate",
    title: S.DOC_MODIFIED_DATE_COL,
    style: { width: "120px" },

    render: item => {
      return iso8601ToShort(item.modifiedDate);
    },
  },
];

interface ConvertModalProps {
  proposalID: number;
  open: boolean;
  handleConfirm: (
    proposalID: number,
    carrierTypeID?: number,
    occupationClassTypeID?: number,
    designTypeID?: number,
    productName?: string,
    fileIdList?: number[],
  ) => void;
  handleReject: () => void;
}

const itemsToMap = keyBy("value");

export const ConvertModal = (props: ConvertModalProps) => {
  const [selectedPage, setSelectedPage] = useState(0);
  const [selectedClassificationIndex, setSelectedClassificationIndex] = useState(-1);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filesResult, setFilesResult] = useState<IDocument[]>([]);
  const dispatch = useDispatch();
  const { open, handleReject, proposalID } = props;

  useEffect(() => {
    if (open) {
      dispatch(fetchProductBuilderOptions());
      dispatch(fetchProposalClassifications(`${proposalID}`));

      // preload files
      (async () => {
        const result = await dispatch(fetchDocuments(`${proposalID}`));
        const data = get(result, "value.data");
        setFilesResult(data as IDocument[]);
      })();
    }
  }, [open]);

  const isFirstPage = selectedPage === 0;

  useEffect(() => {
    setSelectedClassificationIndex(-1);
    setSelectedPage(0);
    setSelectedFiles([]);
  }, [proposalID]);

  const { proposal, carrierOptions, designOptions, occClassOptions, isLoadingClassification, documentation } =
    useSelector((state: IRootState) => ({
      isLoadingClassification: state.proposalOptions.isLoadingClassification,
      proposal: state.proposalOptions.proposalMap[proposalID],
      carrierOptions: itemsToMap(state.productBuilder.carrierTypeList),
      designOptions: itemsToMap(state.productBuilder.designTypeList),
      occClassOptions: itemsToMap(state.productBuilder.occupationClassTypeList),
      documentation: state.documentation,
    }));

  const handleConfirm = () => {
    if (isFirstPage) {
      setSelectedPage(1);
      return;
    }
    if (selectedClassificationIndex !== -1) {
      const classification = proposal?.classifications[selectedClassificationIndex];
      const selectedDesign = (designOptions as any)[classification.designTypeID];
      props.handleConfirm(
        proposalID,
        classification.carrierTypeID,
        classification.occupationClassTypeID,
        classification.designTypeID,
        selectedDesign?.key || "",
        selectedFiles,
      );
    } else {
      props.handleConfirm(proposalID);
    }
  };

  const toggleSelected = (i: number) => {
    setSelectedClassificationIndex(i !== selectedClassificationIndex ? i : -1);
  };

  const classifications = proposal?.classifications.filter(x => !!x.carrierTypeID) || [];
  const filler = new Array(5 - classifications.length).fill("");

  return (
    <PromptModal
      open={open}
      title={isFirstPage ? S.PC_CONVERT_TITLE : S.PC_CONVERT_FILES_TITLE}
      confirmText={isFirstPage ? S.PC_CONVERT_NEXT_BUTTON : S.PC_CONVERT_CONFIRM_BUTTON}
      rejectText={S.PC_CONVERT_CANCEL_BUTTON}
      onReject={handleReject}
      onConfirm={handleConfirm}>
      <div className="convert-modal">
        {selectedPage === 0 && (
          <div className="convert-modal__card_container">
            {isLoadingClassification ? (
              S.LOADING
            ) : classifications.length === 0 ? (
              <div>{S.PC_CONVERT_NO_CLASS}</div>
            ) : (
              <>
                {classifications.map((c, i) => (
                  <div
                    key={i}
                    className={`convert-modal__card ${i == selectedClassificationIndex ? "selected" : ""}`}
                    onClick={() => toggleSelected(i)}>
                    <label>{S.CAMP_CARRIER_NAME}</label>
                    <span>{carrierOptions[c.carrierTypeID]?.key}</span>
                    <label>{S.ANL_OCC_CLASS_LABEL}</label>
                    <span>{occClassOptions[c.occupationClassTypeID]?.key}</span>
                    <label>{S.PROD_COL_PRODUCT_TYPE}</label>
                    <span>{designOptions[c.designTypeID]?.key}</span>
                    <button
                      className={`button-v2 ${i == selectedClassificationIndex ? "button-v2__primary" : "button-v2__lightgray"} button-v2__mini mt-5`}
                      onClick={() => toggleSelected(i)}>
                      Select
                    </button>
                  </div>
                ))}
                {filler.map((c, i) => (
                  <div key={i} className={`convert-modal__card disabled`}></div>
                ))}
              </>
            )}
          </div>
        )}

        {selectedPage === 1 && (
          <DisTable<IDocument>
            columns={columns(selectedFiles, setSelectedFiles)}
            initialState={{}}
            isPaginated={false}
            onUpdate={async ({ page, rows, sort, query }) => {
              return {
                total: 0,
                data: filesResult,
              };
            }}
          />
        )}
      </div>
    </PromptModal>
  );
};
